import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectSelectedComponent, updateComponent } from '../../../redux/editorSlice';
import { PaddingControl, BorderRadiusControl, StyleControl } from '../CommonProperties';
import { SketchPicker } from 'react-color';
import { extractValueAndUnit } from '../utils/styleUtils';
import { usePropertyPanel } from '../PropertyPanelContext';

const TrendingItemsProperties = () => {
  const selectedComponent = useSelector(selectSelectedComponent);
  const dispatch = useDispatch();
  const { handleImageUpload } = usePropertyPanel();
  
  // 디바이스 속성 값 가져오기
  const getDevicePropertyValue = (obj, prop, defaultValue) => {
    if (!obj) return defaultValue;
    return obj[prop] !== undefined ? obj[prop] : defaultValue;
  };

  // 컴포넌트 속성 변경 핸들러
  const handleComponentPropsChange = (property, value) => {
    const existingProps = JSON.parse(JSON.stringify(selectedComponent.componentProps || {}));
    existingProps[property] = value;
    
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        componentProps: existingProps
      }
    }));
  };

  // 스타일 변경 핸들러
  const handleStyleChange = (property, value) => {
    dispatch(updateComponent({
      id: selectedComponent.id,
      changes: {
        style: {
          ...selectedComponent.style,
          [property]: value
        }
      }
    }));
  };

  return (
    <>
      <div className="property-group">
        <div className="property-group-title">
          <i className="fas fa-chart-line"></i> 트렌딩 아이템 설정
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>제목</label>
          <input 
            type="text" 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'title', 'Trending now')}
            onChange={(e) => handleComponentPropsChange('title', e.target.value)}
          />
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>부제목</label>
          <input 
            type="text" 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'subtitle', '에디터가 선정한')}
            onChange={(e) => handleComponentPropsChange('subtitle', e.target.value)}
          />
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>이미지 둥글기</label>
          <div className="input-with-unit">
            {(() => {
              const imageRadius = getDevicePropertyValue(selectedComponent.componentProps, 'imageRadius', '4px');
              const { value, unit } = extractValueAndUnit(imageRadius);
              return (
                <>
                  <input 
                    type="number" 
                    value={value} 
                    onChange={(e) => handleComponentPropsChange('imageRadius', `${e.target.value}${unit}`)}
                  />
                  <select 
                    value={unit}
                    onChange={(e) => {
                      const newUnit = e.target.value;
                      handleComponentPropsChange('imageRadius', `${value}${newUnit}`);
                    }}
                  >
                    <option value="px">px</option>
                    <option value="%">%</option>
                    <option value="rem">rem</option>
                  </select>
                </>
              );
            })()}
          </div>
        </div>
      </div>
      
      {/* MORE 버튼 설정 추가 */}
      <div className="property-group">
        <div className="property-group-title">
          <i className="fas fa-external-link-alt"></i> MORE 버튼 설정
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>버튼 표시</label>
          <input 
            type="checkbox" 
            checked={getDevicePropertyValue(selectedComponent.componentProps, 'showMoreButton', true)}
            onChange={(e) => handleComponentPropsChange('showMoreButton', e.target.checked)}
          />
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>버튼 텍스트</label>
          <input 
            type="text" 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonText', 'MORE')}
            onChange={(e) => handleComponentPropsChange('moreButtonText', e.target.value)}
          />
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>버튼 URL</label>
          <input 
            type="text" 
            value={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonUrl', '#')}
            onChange={(e) => handleComponentPropsChange('moreButtonUrl', e.target.value)}
            placeholder="예: /products 또는 https://example.com"
          />
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>버튼 배경색</label>
          <div className="color-picker-container">
            <SketchPicker 
              color={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonBgColor', '#f8f8f8')}
              onChange={(color) => handleComponentPropsChange('moreButtonBgColor', color.hex)}
              presetColors={['#f8f8f8', '#e0e0e0', '#d2c3aa', '#8a7a6d', '#333333']}
              disableAlpha={false}
            />
          </div>
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>버튼 텍스트 색상</label>
          <div className="color-picker-container">
            <SketchPicker 
              color={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonTextColor', '#8a7a6d')}
              onChange={(color) => handleComponentPropsChange('moreButtonTextColor', color.hex)}
              presetColors={['#8a7a6d', '#333333', '#666666', '#ffffff', '#000000']}
              disableAlpha={false}
            />
          </div>
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>호버 배경색</label>
          <div className="color-picker-container">
            <SketchPicker 
              color={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonHoverBgColor', '#8a7a6d')}
              onChange={(color) => handleComponentPropsChange('moreButtonHoverBgColor', color.hex)}
              presetColors={['#8a7a6d', '#6a5a4d', '#333333', '#000000', '#d2c3aa']}
              disableAlpha={false}
            />
          </div>
        </div>
        
        <div className="property-group">
          <label style={{ minWidth: '30%' }}>호버 텍스트 색상</label>
          <div className="color-picker-container">
            <SketchPicker 
              color={getDevicePropertyValue(selectedComponent.componentProps, 'moreButtonHoverTextColor', '#ffffff')}
              onChange={(color) => handleComponentPropsChange('moreButtonHoverTextColor', color.hex)}
              presetColors={['#ffffff', '#f8f8f8', '#e0e0e0', '#000000', '#333333']}
              disableAlpha={false}
            />
          </div>
        </div>
      </div>
      
      {/* 아이템 설정 */}
      <div className="property-group">
        <div className="property-group-title">
          <i className="fas fa-th-large"></i> 아이템 설정
        </div>
        
        {(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || []).map((item, index) => (
          <div key={index} className="item-editor" style={{ 
            border: '1px solid #ddd',
            borderRadius: '4px',
            padding: '10px',
            marginBottom: '10px'
          }}>
            <div className="property-group-title" style={{ marginTop: 0 }}>
              아이템 #{index+1}
            </div>
            
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>이미지 URL</label>
              <div className="image-url-input-container" style={{ display: 'flex', gap: '10px' }}>
                <input 
                  type="text" 
                  value={item.image} 
                  onChange={(e) => {
                    const newItems = [...(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [])];
                    newItems[index] = { ...newItems[index], image: e.target.value };
                    handleComponentPropsChange('items', newItems);
                  }}
                  style={{ flex: 1 }}
                />
                <label className="upload-button" style={{ 
                  padding: '8px 12px', 
                  backgroundColor: '#4a90e2', 
                  color: 'white', 
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}>
                  업로드
                  <input 
                    type="file" 
                    style={{ display: 'none' }} 
                    accept="image/*"
                    onChange={async (e) => {
                      try {
                        const imageUrl = await handleImageUpload(e);
                        if (imageUrl) {
                          const newItems = [...(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [])];
                          newItems[index] = { ...newItems[index], image: imageUrl };
                          handleComponentPropsChange('items', newItems);
                        }
                      } catch (error) {
                        console.error("이미지 업로드 오류:", error);
                        alert("이미지 업로드 중 오류가 발생했습니다.");
                      }
                    }}
                  />
                </label>
              </div>
            </div>
            
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>제목</label>
              <input 
                type="text" 
                value={item.title} 
                onChange={(e) => {
                  const newItems = [...(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [])];
                  newItems[index] = { ...newItems[index], title: e.target.value };
                  handleComponentPropsChange('items', newItems);
                }}
              />
            </div>
            
            <div className="property-group">
              <label style={{ minWidth: '30%' }}>링크 URL</label>
              <input 
                type="text" 
                value={item.url} 
                onChange={(e) => {
                  const newItems = [...(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [])];
                  newItems[index] = { ...newItems[index], url: e.target.value };
                  handleComponentPropsChange('items', newItems);
                }}
              />
            </div>
          </div>
        ))}
        
        <button
          onClick={() => {
            const items = getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [];
            const newItem = {
              id: Date.now(),
              image: "https://via.placeholder.com/400x400",
              title: "새 아이템",
              url: "#"
            };
            handleComponentPropsChange('items', [...items, newItem]);
          }}
          style={{
            padding: '8px 12px',
            backgroundColor: '#4CAF50', 
            color: 'white', 
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            width: '100%',
            marginTop: '10px'
          }}
        >
          아이템 추가
        </button>
        
        {(getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || []).length > 0 && (
          <button 
            onClick={() => {
              const items = getDevicePropertyValue(selectedComponent.componentProps, 'items', []) || [];
              if (items.length > 0) {
                const newItems = items.slice(0, -1);
                handleComponentPropsChange('items', newItems);
              }
            }}
            style={{ 
              padding: '8px 12px', 
              backgroundColor: '#f44336', 
              color: 'white', 
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '100%',
              marginTop: '10px'
            }}
          >
            마지막 아이템 삭제
          </button>
        )}
      </div>
{/* 
      <PaddingControl />
      <BorderRadiusControl />
      <StyleControl /> */}
    </>
  );
};

export default TrendingItemsProperties; 